import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { api, ApiError, request } from "../api";
import { fmtDate, fmtINR } from "../fmt";

interface Account {
  id: string;
  name: string;
  kind: string;
}
interface Party {
  id: string;
  name: string;
}
interface Category {
  id: string;
  name: string;
  kind: string;
}

interface PreviewRow {
  id: string;
  rowNo: number;
  date: string;
  amount: number;
  drcr: "dr" | "cr";
  narration: string | null;
  matchedPartyId: string | null;
  matchedCategoryId: string | null;
  include: boolean;
}

interface RowEditState {
  partyId: string;
  categoryId: string;
  include: boolean;
  saveRule: boolean;
  rulePattern: string;
}

interface PendingBatch {
  id: string;
  createdAt: string;
  status: string;
  rowsFound: number;
  accountName: string | null;
  documentTitle: string | null;
}

// The api's PresetIdSchema union (@xynex-money/shared/parsers) -- hardcoded
// here rather than imported, since that subpath barrel also re-exports the
// xlsx/pdf parsers (exceljs/pdf-parse), which are Node-only and would bloat
// or break the browser bundle for two string literals.
const PRESETS: { id: string; label: string }[] = [
  { id: "hdfc_icici_sbi_xlsx", label: "HDFC/ICICI/SBI (xlsx)" },
  { id: "csv_generic", label: "Generic CSV" },
];

export function Import() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [accountId, setAccountId] = useState("");
  const [preset, setPreset] = useState(PRESETS[0].id);
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [batchId, setBatchId] = useState<string | null>(null);
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [edits, setEdits] = useState<Record<string, RowEditState>>({});
  const [result, setResult] = useState<{ inserted: number; duplicates: number } | null>(null);

  const [pendingBatches, setPendingBatches] = useState<PendingBatch[]>([]);

  const refreshPendingBatches = () => {
    api
      .get<PendingBatch[]>("/api/imports?status=preview")
      .then(setPendingBatches)
      .catch(() => {
        /* non-fatal: the upload form still works without the pending list */
      });
  };

  useEffect(() => {
    Promise.all([api.get<Account[]>("/api/accounts"), api.get<Party[]>("/api/parties"), api.get<Category[]>("/api/categories")])
      .then(([a, p, c]) => {
        setAccounts(a);
        setParties(p);
        setCategories(c);
        if (a[0]) setAccountId(a[0].id);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load reference data"));
    refreshPendingBatches();
  }, []);

  /** Loads a preview batch's rows into the grid/edit state below -- shared by
   * a fresh upload and by resuming a pending preview (Finding I1). */
  const applyPreview = (newBatchId: string, newRows: PreviewRow[]) => {
    setBatchId(newBatchId);
    setRows(newRows);
    const initialEdits: Record<string, RowEditState> = {};
    for (const r of newRows) {
      initialEdits[r.id] = {
        partyId: r.matchedPartyId ?? "",
        categoryId: r.matchedCategoryId ?? "",
        include: r.include,
        saveRule: false,
        rulePattern: r.narration ?? "",
      };
    }
    setEdits(initialEdits);
  };

  const onUpload = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);
    if (!file || !accountId) {
      setError("account and file are required");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("accountId", accountId);
      fd.append("preset", preset);
      const res = await request<{ batchId: string; rowsFound: number; rows: PreviewRow[] }>("/api/imports", {
        method: "POST",
        body: fd,
      });
      applyPreview(res.batchId, res.rows);
      refreshPendingBatches();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "upload failed");
    } finally {
      setBusy(false);
    }
  };

  /** Resumes a previously-uploaded, not-yet-committed batch from the
   * "Pending previews" list below into the same edit/commit grid. */
  const onResumePending = async (id: string) => {
    setError(null);
    setResult(null);
    setBusy(true);
    try {
      const res = await api.get<{ id: string; rows: PreviewRow[] }>(`/api/imports/${id}`);
      applyPreview(res.id, res.rows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "failed to load pending preview");
    } finally {
      setBusy(false);
    }
  };

  const updateEdit = (rowId: string, patch: Partial<RowEditState>) => {
    setEdits((prev) => ({ ...prev, [rowId]: { ...prev[rowId], ...patch } }));
  };

  const onCommit = async () => {
    if (!batchId) return;
    setError(null);
    setBusy(true);
    try {
      const editList = rows.map((r) => {
        const e = edits[r.id];
        return {
          rowId: r.id,
          partyId: e.partyId || null,
          categoryId: e.categoryId || null,
          include: e.include,
          ...(e.saveRule && e.rulePattern ? { saveRule: true, rulePattern: e.rulePattern } : {}),
        };
      });
      const res = await api.post<{ inserted: number; duplicates: number }>(`/api/imports/${batchId}/commit`, {
        edits: editList,
      });
      setResult(res);
      setBatchId(null);
      setRows([]);
      setEdits({});
      setFile(null);
      refreshPendingBatches();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "commit failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <h1>Import statement</h1>
      {error && <div className="error-text">{error}</div>}
      {result && (
        <div className="card" style={{ marginBottom: "1rem" }}>
          Committed: {result.inserted} inserted, {result.duplicates} duplicate(s) skipped.
        </div>
      )}

      {!batchId && pendingBatches.length > 0 && (
        <div className="card" style={{ marginBottom: "1rem" }}>
          <strong>Pending previews</strong>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Uploaded</th>
                  <th>Account</th>
                  <th>Statement</th>
                  <th className="num">Rows</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {pendingBatches.map((b) => (
                  <tr key={b.id}>
                    <td>{fmtDate(b.createdAt)}</td>
                    <td>{b.accountName ?? "-"}</td>
                    <td>{b.documentTitle ?? "-"}</td>
                    <td className="num">{b.rowsFound}</td>
                    <td>
                      <button type="button" disabled={busy} onClick={() => onResumePending(b.id)}>
                        Resume
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!batchId && (
        <form onSubmit={onUpload} className="toolbar">
          <div className="field">
            <label htmlFor="i-account">Account</label>
            <select id="i-account" required value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.kind})
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="i-preset">Preset</label>
            <select id="i-preset" value={preset} onChange={(e) => setPreset(e.target.value)}>
              {PRESETS.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="i-file">Statement file</label>
            <input
              id="i-file"
              type="file"
              accept=".xlsx,.csv"
              required
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <button className="primary" type="submit" disabled={busy}>
            {busy ? "Uploading..." : "Upload & preview"}
          </button>
        </form>
      )}

      {batchId && (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Include</th>
                  <th>Date</th>
                  <th>Narration</th>
                  <th className="num">Amount</th>
                  <th>Dr/Cr</th>
                  <th>Party</th>
                  <th>Category</th>
                  <th>Save rule</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const e = edits[r.id];
                  if (!e) return null;
                  return (
                    <tr key={r.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={e.include}
                          onChange={(ev) => updateEdit(r.id, { include: ev.target.checked })}
                        />
                      </td>
                      <td>{fmtDate(r.date)}</td>
                      <td>{r.narration ?? "-"}</td>
                      <td className="num">{fmtINR(r.amount)}</td>
                      <td>{r.drcr}</td>
                      <td>
                        <select value={e.partyId} onChange={(ev) => updateEdit(r.id, { partyId: ev.target.value })}>
                          <option value="">-- none --</option>
                          {parties.map((p) => (
                            <option key={p.id} value={p.id}>
                              {p.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <select
                          value={e.categoryId}
                          onChange={(ev) => updateEdit(r.id, { categoryId: ev.target.value })}
                        >
                          <option value="">-- none --</option>
                          {categories.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <label style={{ display: "flex", gap: "0.3rem", alignItems: "center" }}>
                          <input
                            type="checkbox"
                            checked={e.saveRule}
                            onChange={(ev) => updateEdit(r.id, { saveRule: ev.target.checked })}
                          />
                          <input
                            style={{ width: 120 }}
                            value={e.rulePattern}
                            disabled={!e.saveRule}
                            onChange={(ev) => updateEdit(r.id, { rulePattern: ev.target.value })}
                          />
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="modal-actions" style={{ marginTop: "1rem" }}>
            <button
              type="button"
              onClick={() => {
                setBatchId(null);
                setRows([]);
                setEdits({});
              }}
            >
              Cancel
            </button>
            <button className="primary" type="button" disabled={busy} onClick={onCommit}>
              {busy ? "Committing..." : `Commit ${rows.length} rows`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
